import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import unescapedText from 'I18n/utils/unescapedText';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { GET_DISPLAY_NAME } from '../queries/reviewerDisplayNameQuery';
import { useGetUserInsightsQuery } from '../../__generated__/chirp/com/hubspot/marketplace/insights/rpc/UserInsightsChirpService.hooks';
import { SUBMIT_REVIEW } from '../queries/submitReviewQuery';
import { EDIT_REVIEW } from '../queries/editReviewQuery';
import { GET_EDITABILITY } from '../queries/editabilityQuery';
import EcosystemReview from '../records/Review';
import { jsx as _jsx } from "react/jsx-runtime";
export function useEcosystemReviews({
  customValidationErrors,
  defaultReview,
  disableAlerts = false,
  editedReview,
  editReason,
  errorAlertMessage,
  errorAlertTitle,
  offeringId,
  onChange,
  onFail,
  onSuccess,
  reviewFormatter,
  reviewType,
  overrideSubmitValidation,
  successAlertMessage,
  successAlertTitle,
  validationSchema
}) {
  const [submitReview] = useMutation(SUBMIT_REVIEW);
  const [editReview] = useMutation(EDIT_REVIEW);
  const isEditing = !!editedReview;
  const reviewId = editedReview && editedReview.get('id');
  const [hasEdits, setHasEdits] = useState(false);
  const [displayName, setDisplayName] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [marketplaceIndustry, setMarketplaceIndustry] = useState(null);
  const [formattedValidationSchema, setFormattedValidationSchema] = useState(validationSchema);
  const originalReviewRef = useRef(undefined);
  let finalizedDefaultReview;
  if (isEditing && editedReview) {
    const {
      answers,
      ratings,
      tags
    } = editedReview.toJSON();
    finalizedDefaultReview = {
      content: answers,
      ratings,
      tags
    };
  } else {
    finalizedDefaultReview = defaultReview;
  }
  const {
    data: displayNameData
  } = useQuery(GET_DISPLAY_NAME);
  const {
    data: userInsightsData
  } = useGetUserInsightsQuery();
  const {
    data: editabilityData
  } = useQuery(GET_EDITABILITY, {
    variables: {
      reviewId
    },
    skip: !isEditing
  });
  useEffect(() => {
    if (displayNameData) {
      const {
        displayName: {
          displayName: fetchedDisplayName
        }
      } = displayNameData;
      setDisplayName(fetchedDisplayName);
    }
  }, [displayNameData]);
  useEffect(() => {
    if (userInsightsData) {
      const {
        employeeSize,
        industry: fetchedIndustry
      } = userInsightsData.portalInsights;
      setEmployees(employeeSize || null);
      setMarketplaceIndustry(fetchedIndustry || null);
    }
  }, [userInsightsData]);
  useEffect(() => {
    const newValidationSchema = structuredClone(validationSchema);
    if (editabilityData) {
      Object.entries(editabilityData.editability).forEach(([reviewField, editability]) => {
        const schemaKey = reviewField === 'answers' ? 'content' : reviewField;
        if (!newValidationSchema[schemaKey]) {
          newValidationSchema[schemaKey] = {};
        }
        Object.keys(editability).forEach(fieldKey => {
          newValidationSchema[schemaKey][fieldKey] = Object.assign({}, newValidationSchema[schemaKey][fieldKey], {
            locked: !editability[fieldKey]
          });
        });
      });
    }
    setFormattedValidationSchema(newValidationSchema);
  }, [editabilityData, validationSchema]);
  const handleChange = useCallback(review => {
    if (isEditing && editedReview) {
      // Lazily snapshot the original review on the first change so that
      // hasEdits comparisons work whether editedReview was available at
      // mount or arrived later via async fetch.
      if (!originalReviewRef.current) {
        const {
          answers,
          ratings,
          tags
        } = editedReview.toJSON();
        originalReviewRef.current = {
          content: answers,
          ratings,
          tags
        };
      }
      const original = originalReviewRef.current;
      let hasChanges = false;
      Object.entries(review).forEach(([typeKey, typeObj]) => {
        Object.entries(typeObj).forEach(([key, value]) => {
          if (!original[typeKey][key] && value) {
            hasChanges = true;
          } else if (value && original[typeKey][key] !== value) {
            hasChanges = true;
          }
        });
      });
      setHasEdits(hasChanges);
    }
    if (onChange) {
      onChange(review);
    }
  }, [editedReview, isEditing, onChange]);
  const handleSubmit = useCallback(review => {
    let filteredReview = structuredClone(review);
    Object.entries(filteredReview.content).forEach(([key, value]) => {
      if (!value) {
        delete filteredReview.content[key];
      }
    });
    if (reviewFormatter) {
      filteredReview = reviewFormatter(filteredReview);
    }
    let submittedReview = new EcosystemReview(Object.assign({}, filteredReview, {
      answers: filteredReview.content
    })).set('createdAt', new Date().valueOf()).set('entityId', offeringId).set('reviewType', reviewType).set('reviewerDisplayName', displayName).set('employees', employees).set('marketplaceIndustry', marketplaceIndustry);
    if (isEditing && reviewId) {
      submittedReview = submittedReview.set('id', reviewId);
    }
    const submitFunction = isEditing ? editReview : submitReview;
    submitFunction({
      variables: {
        review: submittedReview,
        editReason
      }
    }).then(() => {
      if (!disableAlerts) {
        FloatingAlertStore.addAlert({
          sticky: true,
          message: successAlertMessage !== null ? successAlertMessage || /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "marketplace-ui-reviews.wizard.alert.submit.message"
          }) : undefined,
          titleText: successAlertTitle !== null ? successAlertTitle || unescapedText('marketplace-ui-reviews.wizard.alert.submit.title') : undefined,
          type: 'success'
        });
      }
      if (onSuccess) {
        onSuccess(filteredReview);
      }
    }).catch(e => {
      // Always parse the backend error so we can capture malformed
      // responses to Raven regardless of whether the floating alert UI
      // is suppressed (e.g. on the external review surface).
      let parsedSubCategory;
      try {
        parsedSubCategory = JSON.parse(e.data).subCategory;
      } catch (err) {
        Raven.captureMessage('subCategory not provided in backend error response ', {
          level: 'warning',
          extra: {
            error: err
          }
        });
      }
      if (!disableAlerts) {
        let message = errorAlertMessage || unescapedText('marketplace-ui-reviews.wizard.alert.error.message');
        if (parsedSubCategory && isValidI18nKey(`marketplace-ui-reviews.error.${parsedSubCategory}`)) {
          message = unescapedText(`marketplace-ui-reviews.error.${parsedSubCategory}`);
        }
        FloatingAlertStore.addAlert({
          message,
          titleText: errorAlertTitle !== null ? errorAlertTitle || unescapedText('marketplace-ui-reviews.wizard.alert.error.title') : undefined,
          type: 'danger'
        });
      }
      if (onFail) {
        onFail();
      }
    });
  }, [disableAlerts, displayName, editReason, editReview, employees, errorAlertMessage, errorAlertTitle, isEditing, offeringId, marketplaceIndustry, onFail, onSuccess, reviewFormatter, reviewId, reviewType, successAlertMessage, successAlertTitle, submitReview]);
  const formattedCustomValidationErrors = customValidationErrors ? [...customValidationErrors] : [];
  if (!overrideSubmitValidation && isEditing && !hasEdits) {
    formattedCustomValidationErrors.push(unescapedText('marketplace-ui-reviews.wizard.customValidation.noEdits'));
  }
  if (!overrideSubmitValidation && isEditing && !editReason) {
    formattedCustomValidationErrors.push(unescapedText('marketplace-ui-reviews.wizard.customValidation.noEditReason'));
  }
  return {
    finalizedDefaultReview,
    formattedCustomValidationErrors,
    formattedValidationSchema,
    handleChange,
    handleSubmit
  };
}